import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { UPDATE_USER_PREFERENCES_MUTATION } from '../graphql/resolvers/userMutations/updateUserPreferences.gql';
import { GET_USER_QUERY } from '../graphql/resolvers/userQueries/getUser.gql';

const STORAGE_KEY = 'trilirivos-nav-collapsed';
const DEFAULT_COLLAPSED = true;

function readLocalStorage(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return DEFAULT_COLLAPSED;
  } catch {
    return DEFAULT_COLLAPSED;
  }
}

function writeLocalStorage(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* localStorage unavailable (private mode, full quota); ignore */
  }
}

interface UseNavCollapsedReturn {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (next: boolean) => void;
}

/**
 * TRIL-126: hybrid persistence for the nav rail collapsed/expanded state.
 *
 * Sources of truth, resolved in order:
 *   1. localStorage (first paint — instant, no flash)
 *   2. UserPreferences.navCollapsed from Apollo cache (server canonical;
 *      syncs cross-device on the next getUser response)
 *   3. User toggle clicks (writes both localStorage + updateUserPreferences
 *      mutation in parallel; UI never blocks on the server round-trip)
 *
 * Unauthenticated users: server writes are skipped (mutation never fires).
 * localStorage carries the preference until they sign in.
 */
export function useNavCollapsed(): UseNavCollapsedReturn {
  const { isSignedIn, user } = useAuth();
  const userId = user?.id;
  const [collapsed, setCollapsedLocal] = useState<boolean>(readLocalStorage);

  // Read server-canonical value via Apollo cache. Skip when unauthenticated
  // or before we have a userId — the query requires it as a variable.
  const { data } = useQuery(GET_USER_QUERY, {
    variables: { userId: userId as string },
    skip: !isSignedIn || !userId,
    fetchPolicy: 'cache-first',
  });

  // On hydrate: if server differs from local, server wins (canonical).
  // `collapsed` is intentionally omitted from deps — including it would re-run
  // the effect on every toggle and clobber the user's intent with the cached
  // server value.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    const serverValue = data?.getUser?.userPreferences?.navCollapsed;
    if (typeof serverValue === 'boolean' && serverValue !== collapsed) {
      setCollapsedLocal(serverValue);
      writeLocalStorage(serverValue);
    }
  }, [data]);

  const [updatePrefs] = useMutation(UPDATE_USER_PREFERENCES_MUTATION);

  const setCollapsed = (next: boolean): void => {
    setCollapsedLocal(next);
    writeLocalStorage(next);
    if (isSignedIn && userId) {
      updatePrefs({
        variables: { input: { userId, navCollapsed: next } },
      }).catch(() => {
        /* Server failure leaves UI in user-intended state */
      });
    }
  };

  const toggle = (): void => setCollapsed(!collapsed);

  return { collapsed, toggle, setCollapsed };
}
